import { BUYER_SUPPORTED_PHONE_NUMBERS, SUPPORTED_COUNTRIES } from '@application/constants';
import { useValidation } from '@application/utils';
import { useMemo } from 'react';
import { AccountCreatedFromEnum } from '@gql/graphql';
import type { CountryCode } from '@gql/graphql';
import { boolean, mixed, object, ref, string } from 'yup';
import type { JoinBuyerFromControllerProps } from './types';

interface UseValidationSchemaParams extends Pick<JoinBuyerFromControllerProps, 'createdFrom'> {
  showPostSignup: boolean;
}
export const useValidationSchema = (params: UseValidationSchemaParams) => {
  const { showPostSignup, createdFrom } = params;

  const {
    emailMessage,
    minCharactersMessage,
    passwordDifferentMessage,
    invalidMessage,
    requiredMessage,
    isValidEmail,
  } = useValidation();

  const postSignUpSchema = useMemo(() => {
    return object({
      phonePrefixCountry: string().oneOf(BUYER_SUPPORTED_PHONE_NUMBERS).required(requiredMessage),
      phoneNumber: string()
        .test('is-valid', invalidMessage, (value, context) => {
          if (value === null) {
            return true;
          }
          if (SUPPORTED_COUNTRIES.includes(context.parent.phonePrefixCountry)) {
            if (context.parent.phonePrefixCountry === 'DE') {
              return (
                value?.match(/^0?((\+?)([0-9]){10})$/) || value?.match(/^0?((\+?)([0-9]){11})$/)
              );
            }
            return value?.match(/^0?((\+?)([0-9]){9})$/);
          }
          return !!value;
        })
        .required(requiredMessage),
      country: mixed<CountryCode>().required(requiredMessage),
      otherCountry: string(),
      ...(createdFrom === AccountCreatedFromEnum.APPLICATION ||
      createdFrom === AccountCreatedFromEnum.BOOKING
        ? {
            hasADog: boolean().optional().nullable(),
          }
        : {
            hasADog: boolean().required(requiredMessage),
          }),
    }).defined();
  }, [createdFrom, invalidMessage, requiredMessage]);

  const validationSchema = useMemo(() => {
    if (showPostSignup) {
      return postSignUpSchema;
    }

    return object({
      firstName: string().required(requiredMessage),
      lastName: string().required(requiredMessage),
      email: string()
        .email(emailMessage)
        .required(requiredMessage)
        .test('is-valid-email', emailMessage, (value) => isValidEmail(value)),

      password: string().min(6, minCharactersMessage(6)).required(requiredMessage),
      confirmPassword: string()
        .oneOf([ref('password'), null], passwordDifferentMessage)
        .required(requiredMessage),
      acceptedTerms: boolean().isTrue(requiredMessage).required(requiredMessage),
      marketingEmailConsent: boolean().optional(),
    })
      .concat(postSignUpSchema)
      .defined();
  }, [
    showPostSignup,
    requiredMessage,
    emailMessage,
    minCharactersMessage,
    passwordDifferentMessage,
    postSignUpSchema,
    isValidEmail,
  ]);

  return { validationSchema, postSignUpSchema };
};
